// Ad attribution for the web2app funnel's in-house checkout (the paywall +
// Stripe steps at the end of /funnel-auth). This is the
// browser half of what RevenueCat's hosted funnel did for us: hold on to the
// click that brought the parent here, hand it to the backend once they have an
// account, and fire the browser-side conversion pixels with ids the server's
// Conversions API events share, so each network counts the purchase once.
//
//  - captureAdClicks(): on landing, keep fbclid / gclid (+ gbraid / wbraid)
//    and the utm_* params from the URL. The funnel's Google OAuth round trip
//    comes back to a URL without them, so they live in localStorage, newest
//    click wins, for as long as each network can still attribute it.
//  - funnelAttribution(): everything the backend stores against the account
//    for the server-side events: TikTok (utils/tiktokPixel), Meta `_fbp`/`_fbc`
//    cookies (or `_fbc` rebuilt from fbclid the way Meta specifies), gclid,
//    UTMs and the landing page.
//  - metaTrack(): the Meta Pixel's funnel-step events (InitiateCheckout). The
//    purchase itself is reported to Meta by RevenueCat, never by the page.
//    The server sends the same event with the same event_id (see
//    Pixley-Backend/integrations/meta_capi.py), so Meta dedups the pair.
//
// The Meta Pixel itself is loaded site-wide from public/index.html (`fbq`);
// nothing here loads it. Everything is wrapped so a blocked pixel or a
// private-mode storage failure can never break the funnel.
import { logger } from './logger';
import { tiktokAttribution } from './tiktokPixel';

export const AD_CLICK_KEY = 'pixley_ad_click';
export const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
// Meta attributes a click for 7 days, Google for up to 90. The stored click is
// kept for the longer window; the server applies each network's own rule.
const CLICK_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;
const TOKEN = /^[!-~]{1,512}$/;
const MAX_UTM_LEN = 200;

// The stored click lives in three places. localStorage is the primary store,
// but the Instagram and Facebook in-app browsers on iOS have been seen to
// drop or refuse it between the landing and the Google sign-in while cookies
// survived (the Meta pixel's _fbc was there, our record was not), so the same
// record also goes into a first-party cookie and sessionStorage, and reads
// fall back in that order.
const COOKIE_MAX_AGE_S = 90 * 24 * 60 * 60;

const cookieDomain = () => {
  try {
    const host = window.location.hostname;
    return /(^|\.)pixleyai\.com$/.test(host) ? '; domain=.pixleyai.com' : '';
  } catch (_) {
    return '';
  }
};

const writeStoredAdClick = (record) => {
  const raw = JSON.stringify(record);
  let written = false;
  try {
    localStorage.setItem(AD_CLICK_KEY, raw);
    written = true;
  } catch (_) {
    // blocked or full: the cookie below still carries it
  }
  try {
    sessionStorage.setItem(AD_CLICK_KEY, raw);
    written = true;
  } catch (_) {
    // same
  }
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${AD_CLICK_KEY}=${encodeURIComponent(raw)}; max-age=${COOKIE_MAX_AGE_S}; path=/${cookieDomain()}; SameSite=Lax${secure}`;
    written = true;
  } catch (_) {
    // cookies off: localStorage / sessionStorage above
  }
  return written;
};

const readCookie = (name) => {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch (_) {
    return null;
  }
};

const token = (value) => (typeof value === 'string' && TOKEN.test(value) ? value : null);

/**
 * Remember the ad click on the landing URL. A fresh click id in the URL always
 * replaces a stored one (a returning parent is matched to the ad they clicked
 * this time); UTMs are only taken together with the click they came with, or
 * on their own when there is no stored click yet.
 */
export function captureAdClicks() {
  try {
    const params = new URLSearchParams(window.location.search);
    const fresh = {};
    for (const key of ['fbclid', 'gclid', 'gbraid', 'wbraid']) {
      const value = token(params.get(key));
      if (value) fresh[key] = value;
    }
    const utm = {};
    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value && value.trim()) utm[key] = value.trim().slice(0, MAX_UTM_LEN);
    }
    const hasClick = Object.keys(fresh).length > 0;
    const stored = readStoredAdClick();
    if (!hasClick && (!Object.keys(utm).length || stored)) return stored;

    const record = {
      ...fresh,
      utm: Object.keys(utm).length ? utm : stored?.utm || null,
      landing_page: `${window.location.origin}${window.location.pathname}`,
      at: Date.now(),
    };
    writeStoredAdClick(record);
    return record;
  } catch (_) {
    return null;
  }
}

const parseStoredAdClick = (raw) => {
  if (!raw) return null;
  try {
    const record = JSON.parse(raw);
    if (!record || typeof record.at !== 'number' || Date.now() - record.at > CLICK_MAX_AGE_MS) return null;
    return record;
  } catch (_) {
    return null;
  }
};

export function readStoredAdClick() {
  let raw = null;
  try { raw = localStorage.getItem(AD_CLICK_KEY); } catch (_) { raw = null; }
  if (!raw) {
    try { raw = sessionStorage.getItem(AD_CLICK_KEY); } catch (_) { raw = null; }
  }
  if (!raw) raw = readCookie(AD_CLICK_KEY);
  const record = parseStoredAdClick(raw);
  if (!record && raw) {
    // expired or malformed: forget it everywhere
    try { localStorage.removeItem(AD_CLICK_KEY); } catch (_) { /* ignore */ }
    try { sessionStorage.removeItem(AD_CLICK_KEY); } catch (_) { /* ignore */ }
    try { document.cookie = `${AD_CLICK_KEY}=; max-age=0; path=/${cookieDomain()}`; } catch (_) { /* ignore */ }
  }
  return record;
}

const GOOGLE_CLICK_KEYS = ['gclid', 'gbraid', 'wbraid'];

/**
 * Whether this visit came from a Google ad: a Google click id or
 * utm_source=google on the URL, or on the stored click. Google-referred
 * visitors are held to the offer the ads state (hooks/useOfferVariant), see
 * its terms on the welcome screen, and reach the App Store with a Google
 * campaign token (FunnelCheckout.appStoreUrl).
 */
export function googleReferred() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (GOOGLE_CLICK_KEYS.some((key) => token(params.get(key)))) return true;
    if ((params.get('utm_source') || '').trim().toLowerCase() === 'google') return true;
    const stored = readStoredAdClick();
    if (!stored) return false;
    if (GOOGLE_CLICK_KEYS.some((key) => stored[key])) return true;
    return (stored.utm?.utm_source || '').toLowerCase() === 'google';
  } catch (_) {
    return false;
  }
}

/**
 * Meta's `_fbc` cookie, or one built from the fbclid the way the Pixel would
 * have (`fb.1.<click time ms>.<fbclid>`) when the Pixel hasn't written it —
 * blocked, or the landing happened before it loaded.
 */
export function metaClickId(stored = readStoredAdClick()) {
  const cookie = readCookie('_fbc');
  if (cookie && TOKEN.test(cookie)) return cookie;
  if (stored?.fbclid) return `fb.1.${stored.at}.${stored.fbclid}`;
  return null;
}

/** What the backend stores against the account for server-side conversions. */
export function funnelAttribution() {
  const out = { ...tiktokAttribution() };
  try {
    const stored = readStoredAdClick();
    const fbc = metaClickId(stored);
    const fbp = readCookie('_fbp');
    if (fbc) out.fbc = fbc;
    if (fbp && TOKEN.test(fbp)) out.fbp = fbp;
    if (stored) {
      for (const key of ['fbclid', 'gclid', 'gbraid', 'wbraid']) {
        if (stored[key]) out[key] = stored[key];
      }
      if (stored.utm) {
        for (const key of UTM_KEYS) {
          if (stored.utm[key]) out[key] = stored.utm[key];
        }
      }
      if (stored.landing_page) out.landing_page = stored.landing_page;
    }
  } catch (_) {
    // attribution is best effort
  }
  return out;
}

/**
 * The id the server uses for the same conversion (meta_capi.py): one per
 * account per conversion type, so a replayed success screen and the server's
 * copy all collapse into one event on Meta's side.
 */
export function metaTrack(event, params = {}, eventId) {
  try {
    if (typeof window.fbq !== 'function') return false;
    if (eventId) window.fbq('track', event, params, { eventID: eventId });
    else window.fbq('track', event, params);
    return true;
  } catch (e) {
    logger.warn('Meta pixel track failed (non-blocking):', e);
    return false;
  }
}

/** Dollar value of what was just bought, for the browser pixels' `value`. */
export const purchaseValue = ({ amountCents, fallback = 0 } = {}) =>
  Number.isFinite(amountCents) ? Math.round(amountCents) / 100 : fallback;
